import { useParams } from 'react-router-dom'
import {
  ArticleBreadcrumbs,
  ArticleByline,
  ArticleCoverImage,
  ArticleH1,
  ArticleHeroImage,
  ArticleLayout,
  ArticleTldr,
  ArticleToc,
} from '@/legacy/components/blog/ArticleKit'
import { ArticleSectionRenderer } from '@/legacy/components/blog/ArticleSectionRenderer'
import { SeoHead } from '@/components/seo/SeoHead'
import { getArticleBySlug } from '@/data/articles'
import type { Article } from '@/domain/article'
import { buildArticleToc } from '@/lib/articleSections'
import { articlePath } from '@/lib/url'
import { articleJsonLd, pageTitle } from '@/lib/seo'
import { sitePages } from '@/config/pages'
import { NotFoundPage } from '@/pages/NotFoundPage'

const defaultHeroPath = '/media/photos/article-components-hero.avif'
const CaseStudyArticlePage = ({ article }: { article: Article }) => {
  const heroImage = article.coverImage?.url ?? defaultHeroPath
  const { toc, headingIds } = buildArticleToc(article.body)

  return (
    <>
      <SeoHead
        title={article.seo?.title ? pageTitle(article.seo.title) : pageTitle(article.title)}
        description={article.seo?.description ?? article.excerpt}
        canonicalPath={articlePath(article.slug)}
        type="article"
        noindex={!sitePages.blog.index}
        fullSnippet
        publishedTime={article.publishedAt}
        modifiedTime={article.updatedAt ?? article.publishedAt}
        jsonLd={articleJsonLd(article)}
      />

      <article className="article-kit-page case-study-page">
        <div className="article-kit-container">
          <ArticleHeroImage
            src={heroImage}
            alt={article.coverImage?.alternativeText ?? 'Accounting workspace with client documents and operating notes'}
          />

          <ArticleLayout toc={<ArticleToc items={toc} />}>
            <header className="article-kit-header case-study-header">
              <div className="article-kit-eyebrow-row">
                <ArticleBreadcrumbs
                  items={[
                    { label: 'Home', href: '/' },
                    { label: 'Blog', href: '/blog' },
                    { label: 'Article' },
                  ]}
                />
              </div>

              <div>
                <ArticleH1>{article.title}</ArticleH1>
              </div>
            </header>
            <ArticleSectionRenderer blocks={article.body} headingIds={headingIds} wrapBlocks />
          </ArticleLayout>
        </div>
      </article>
    </>
  )
}

export const BlogPostPage = () => {
  const { slug = '' } = useParams()
  const article = getArticleBySlug(slug)

  if (!article) {
    return <NotFoundPage />
  }

  if (article.format === 'case-study') {
    return <CaseStudyArticlePage article={article} />
  }

  const heroImage = article.coverImage?.url ?? defaultHeroPath
  const coverAlt =
    article.coverImage?.alternativeText ?? 'Accounting workspace with client documents and operating notes'
  const { toc, headingIds } = buildArticleToc(article.body)
  const breadcrumbs = (
    <div className="article-kit-eyebrow-row">
      <ArticleBreadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'Blog', href: '/blog' },
          { label: 'Article' },
        ]}
      />
    </div>
  )

  return (
    <>
      <SeoHead
        title={article.seo?.title ? pageTitle(article.seo.title) : pageTitle(article.title)}
        description={article.seo?.description ?? article.excerpt}
        canonicalPath={articlePath(article.slug)}
        type="article"
        noindex={!sitePages.blog.index}
        fullSnippet
        publishedTime={article.publishedAt}
        modifiedTime={article.updatedAt ?? article.publishedAt}
        jsonLd={articleJsonLd(article)}
      />

      <article className="article-kit-page blog-article-page">
        <div className="article-kit-container">
          {article.author ? null : <ArticleHeroImage src={heroImage} alt={coverAlt} />}

          <ArticleLayout toc={<ArticleToc items={toc} />}>
            <header className="article-kit-header">
              {breadcrumbs}

              {article.author ? (
                <div>
                  <ArticleH1>{article.title}</ArticleH1>
                  <ArticleByline author={article.author} date={article.publishedAt} />
                  <ArticleCoverImage src={heroImage} alt={coverAlt} />
                  <ArticleTldr label="TLDR">{article.excerpt}</ArticleTldr>
                </div>
              ) : (
                <div>
                  <ArticleH1>{article.title}</ArticleH1>
                  <ArticleByline author={article.author} date={article.publishedAt} />
                  <ArticleTldr label="TLDR">{article.excerpt}</ArticleTldr>
                </div>
              )}
            </header>
            <ArticleSectionRenderer blocks={article.body} headingIds={headingIds} />
          </ArticleLayout>
        </div>
      </article>
    </>
  )
}
